import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useParams } from 'react-router-dom'
import { z } from 'zod'
import { OwnerPasswordSchema } from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import {
  usePasswordResetConsume,
  usePasswordResetValidate,
} from '../hooks/usePasswordReset.js'
import { OwnerThemeToggle } from '../components/OwnerThemeToggle.js'
import { PasswordInput } from '../../shared/PasswordInput.js'

/**
 * /reset-password/:token — owner password reset entry point.
 *
 * Mirrors SetupWizardPage's token validation + password step, but does NOT
 * route into the setup wizard afterwards: on success the owner lands on
 * /dashboard. Tenant.status is untouched by the backend consume route, so
 * an active/suspended tenant stays in its current state.
 *
 * Shown info: the owner's email (read-only, for confirmation) + new
 * password + confirm.
 */

const ResetFormSchema = z
  .object({
    password: OwnerPasswordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  })
type ResetForm = z.infer<typeof ResetFormSchema>

function ExpiredLink({
  reason,
}: {
  reason: 'expired' | 'consumed' | 'not_found'
}) {
  const message =
    reason === 'expired'
      ? 'This password reset link has expired.'
      : reason === 'consumed'
        ? 'This password reset link has already been used.'
        : 'This password reset link is not valid.'
  return (
    <div className="owner-root">
      <div className="owner-wizard">
        <div className="owner-wizard-card">
          <h2>Link unavailable</h2>
          <p className="owner-card-sub" style={{ marginTop: 8 }}>
            {message}
          </p>
          <p className="owner-hint">
            Please contact Naya Pass support and we'll send you a fresh link.
          </p>
        </div>
      </div>
    </div>
  )
}

export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>()
  const validate = usePasswordResetValidate(token)
  const consume = usePasswordResetConsume()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ResetForm>({ resolver: zodResolver(ResetFormSchema) })

  if (!token) return <Navigate to="/login" replace />
  if (validate.isLoading) {
    return (
      <div className="owner-root">
        <p className="owner-loading">Checking your reset link…</p>
      </div>
    )
  }
  if (validate.isError) {
    return (
      <div className="owner-root">
        <div className="owner-error-state">
          <p>We couldn't check your reset link. Please try again.</p>
        </div>
      </div>
    )
  }
  if (validate.data && validate.data.valid === false) {
    return <ExpiredLink reason={validate.data.reason} />
  }
  if (isSubmitSuccessful && consume.isSuccess) {
    return <Navigate to="/dashboard" replace />
  }

  const email =
    validate.data && validate.data.valid ? validate.data.owner.email : null

  function onSubmit(values: ResetForm) {
    consume.mutate(
      { token: token!, password: values.password },
      {
        onError: (err) => {
          setError('root', {
            message:
              err instanceof OwnerApiError
                ? err.userMessage
                : 'Could not reset your password. Please try again.',
          })
        },
      },
    )
  }

  return (
    <div className="owner-root">
      <div className="owner-wizard">
        <div className="owner-wizard-card">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <h2>Reset your password</h2>
            <p className="owner-card-sub">
              Choose a new password to sign in. At least 10 characters.
            </p>

            {email && (
              <div className="owner-field">
                <label className="owner-label">Account</label>
                <p
                  className="owner-card-sub"
                  style={{ margin: 0, fontWeight: 600 }}
                >
                  {email}
                </p>
              </div>
            )}

            {errors.root && (
              <div className="owner-auth-error" role="alert">
                {errors.root.message}
              </div>
            )}

            <div className="owner-field">
              <label className="owner-label" htmlFor="password">
                New password
              </label>
              <PasswordInput
                id="password"
                classPrefix="owner"
                autoComplete="new-password"
                aria-invalid={errors.password ? 'true' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p className="owner-field-error">{errors.password.message}</p>
              )}
            </div>

            <div className="owner-field">
              <label className="owner-label" htmlFor="confirm">
                Confirm new password
              </label>
              <PasswordInput
                id="confirm"
                classPrefix="owner"
                autoComplete="new-password"
                aria-invalid={errors.confirm ? 'true' : undefined}
                {...register('confirm')}
              />
              {errors.confirm && (
                <p className="owner-field-error">{errors.confirm.message}</p>
              )}
            </div>

            <div className="owner-wizard-actions">
              <button
                type="submit"
                className="owner-btn owner-btn-primary"
                disabled={isSubmitting || consume.isPending}
              >
                {consume.isPending ? 'Saving…' : 'Reset password'}
              </button>
            </div>
          </form>
        </div>
        <div style={{ marginTop: 16 }}>
          <OwnerThemeToggle />
        </div>
      </div>
    </div>
  )
}
